/**
 * UnderwritingHub.jsx
 *
 * Lightweight replacement for the heavy Applications Workflow snapshot.
 * Renders empty by default — only loads Monday items once the user picks an
 * Acct Manager (semantically the underwriter). Pages from ~40s mount cost
 * down to <1s.
 *
 * Phase 1A:
 *   - Acct Manager dropdown (with item counts)
 *   - "Show Final UW Checks" toggle
 *   - Filtered items table for the chosen manager
 *   - Clicking a row opens the UnderwritingWorkspace (separate component)
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  RefreshCw,
  UserCircle,
  ExternalLink,
  ClipboardList,
  Sparkles,
} from "lucide-react";

import UnderwritingWorkspace from "./underwriting/UnderwritingWorkspace";


export default function UnderwritingHub() {
  // Manager selection
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [selectedManager, setSelectedManager] = useState("");
  const [includeFinalChecks, setIncludeFinalChecks] = useState(false);
  const [includeConditional, setIncludeConditional] = useState(false);

  // Items
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState("");

  // Workspace
  const [openItemId, setOpenItemId] = useState(null);

  const loadManagers = useCallback(async () => {
    setLoadingManagers(true);
    try {
      const res = await api.get("/underwriting-hub/account-managers", {
        params: { include_final_checks: includeFinalChecks, include_conditional: includeConditional },
      });
      setManagers(res.data?.managers || []);
    } catch (e) {
      console.error("loadManagers", e);
      toast.error("Couldn't load Acct Managers from Monday");
    } finally {
      setLoadingManagers(false);
    }
  }, [includeFinalChecks, includeConditional]);

  const loadItems = useCallback(async () => {
    if (!selectedManager) {
      setItems([]);
      return;
    }
    setLoadingItems(true);
    try {
      const res = await api.get("/underwriting-hub/items", {
        params: { manager: selectedManager, include_final_checks: includeFinalChecks, include_conditional: includeConditional },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      console.error("loadItems", e);
      toast.error("Couldn't load items for " + selectedManager);
    } finally {
      setLoadingItems(false);
    }
  }, [selectedManager, includeFinalChecks, includeConditional]);

  // Auto-load managers on mount + whenever the toggle flips
  useEffect(() => { loadManagers(); }, [loadManagers]);

  // Auto-load items whenever manager / toggle changes
  useEffect(() => { loadItems(); }, [loadItems]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((it) =>
      [it.name, it.sf_number, it.contact_name, it.broker, it.contact_email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="underwriting-hub">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            Underwriting Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pick an Acct Manager to load only their open underwriting deals — no more 30-second page loads.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { loadManagers(); loadItems(); }}
          disabled={loadingManagers || loadingItems}
          data-testid="uw-hub-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loadingManagers || loadingItems ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Filter Bar */}
      <Card className="mb-6">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs font-medium">Acct Manager</Label>
            <Select value={selectedManager} onValueChange={setSelectedManager}>
              <SelectTrigger data-testid="uw-hub-manager-select">
                <SelectValue
                  placeholder={loadingManagers ? "Loading…" : "— pick an Acct Manager —"}
                />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) => (
                  <SelectItem key={m.name} value={m.name} data-testid={`uw-hub-mgr-${m.name}`}>
                    <span className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-muted-foreground" />
                      {m.name}
                      <Badge variant="secondary" className="ml-1">{m.count}</Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="include-final-checks"
              checked={includeFinalChecks}
              onCheckedChange={setIncludeFinalChecks}
              data-testid="uw-hub-toggle-final-checks"
            />
            <Label htmlFor="include-final-checks" className="text-sm cursor-pointer">
              Show Final UW Checks too
            </Label>
          </div>

          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="include-conditional"
              checked={includeConditional}
              onCheckedChange={setIncludeConditional}
              data-testid="uw-hub-toggle-conditional"
            />
            <Label htmlFor="include-conditional" className="text-sm cursor-pointer">
              Show Conditional Offers too
            </Label>
          </div>

          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs font-medium">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="SF#, company, broker…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                disabled={!selectedManager}
                data-testid="uw-hub-search"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Empty state — no manager picked */}
      {!selectedManager && (
        <Card className="text-center py-16 bg-slate-50/50 border-dashed">
          <CardContent>
            <Sparkles className="w-10 h-10 mx-auto text-blue-400 mb-3" />
            <p className="text-base font-medium">Select an Acct Manager to begin</p>
            <p className="text-sm text-muted-foreground mt-1">
              {managers.length === 0 && !loadingManagers
                ? "No managers found in the underwriting queue right now."
                : "Your filtered queue will appear here in under a second."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Items table */}
      {selectedManager && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>
                {selectedManager}&apos;s underwriting queue
                <Badge variant="outline" className="ml-2">{filteredItems.length}</Badge>
              </span>
              {loadingItems && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingItems && items.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading queue…
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {search ? "No items match that search." : "No deals currently in this manager's underwriting queue."}
              </div>
            ) : (
              <Table data-testid="uw-hub-items-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>SF#</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Broker</TableHead>
                    <TableHead className="text-right">Loan</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((it) => (
                    <TableRow
                      key={it.monday_item_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setOpenItemId(it.monday_item_id)}
                      data-testid={`uw-hub-row-${it.monday_item_id}`}
                    >
                      <TableCell className="font-mono text-xs">{it.sf_number || "—"}</TableCell>
                      <TableCell className="font-medium">{it.name}</TableCell>
                      <TableCell>
                        {it.status && (
                          <Badge variant="outline" className="text-xs">
                            {it.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            it.group_id === "topics"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                        >
                          {it.group_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{it.broker || it.broker_direct || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {it.loan_amount ? `£${it.loan_amount.toLocaleString()}` : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {it.updated_at ? new Date(it.updated_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setOpenItemId(it.monday_item_id); }}
                          data-testid={`uw-hub-open-${it.monday_item_id}`}
                        >
                          Open
                          <ExternalLink className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Workspace (modal) */}
      {openItemId && (
        <UnderwritingWorkspace
          mondayItemId={openItemId}
          onClose={() => setOpenItemId(null)}
        />
      )}
    </div>
  );
}
